import { Phone, MapPin, Navigation, TrainFront, Landmark } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";

const LANDMARKS = [
  {
    icon: TrainFront,
    title: "Durgam Cheruvu Metro",
    text: "A short walk from the metro station — easiest way to reach us.",
  },
  {
    icon: Landmark,
    title: "Above HDFC Bank",
    text: "Look for the HDFC Bank branch in Gem Square Building, then head up.",
  },
  {
    icon: MapPin,
    title: "Gem Square Building",
    text: "Pillar No. 1743, Plot No. 1-88/2, 1st Floor, Madhapur.",
  },
];

const Contact = () => {
  return (
    <>
      <SEO
        title="Contact & Location | Blossom Spa Madhapur"
        description="Visit Blossom Spa Madhapur near Durgam Cheruvu Metro, above HDFC Bank, Madhapur, Hyderabad. Call +91 7330619409 — no forms, just call to book."
        path="/contact"
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section className="relative pt-40 pb-20 sm:pt-48 sm:pb-24 bg-spa-slate overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-spa-sage mb-5">
              Find Us
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-spa-cream max-w-2xl">
              Visit Our Sanctuary in Madhapur
            </h1>
            <p className="mt-4 text-spa-cream/70 max-w-lg text-base sm:text-lg">
              No forms, no waiting — just call us directly to book your
              session.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTACT INFO + MAP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Reveal className="bg-spa-muted rounded-3xl p-8 sm:p-10 flex flex-col justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-spa-terracotta mb-6">
              Direct Contact
            </p>

            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-spa-slate text-spa-cream shrink-0 group-hover:bg-spa-slateHover transition-colors">
                <Phone className="w-5 h-5" />
              </span>
              <span className="font-serif text-2xl sm:text-3xl text-spa-slate group-hover:text-spa-terracotta transition-colors">
                {BUSINESS.phoneDisplay}
              </span>
            </a>

            <div className="flex items-start gap-4 mt-8">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-spa-slate text-spa-cream shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <p
                data-testid="contact-address-text"
                className="text-spa-slate text-base leading-relaxed pt-1"
              >
                {BUSINESS.address}
              </p>
            </div>
          </div>

          <a
            href={BUSINESS.mapsDirectionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-directions-button"
            className="inline-flex items-center justify-center gap-2.5 bg-spa-slate text-spa-cream px-7 py-4 rounded-full text-sm font-medium mt-10 hover:bg-spa-slateHover transition-all duration-300 w-fit"
          >
            <Navigation className="w-4 h-4" /> Get Directions
          </a>
        </Reveal>

        <Reveal delay={0.1} className="rounded-3xl overflow-hidden min-h-[380px] border border-spa-slate/10">
          <iframe
            data-testid="contact-map-embed"
            title="Blossom Spa Madhapur Location"
            src={BUSINESS.mapsEmbedSrc}
            width="100%"
            height="100%"
            style={{ border: 0, minHeight: "380px" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </Reveal>
      </section>

      {/* LANDMARKS */}
      <section className="bg-spa-muted py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-spa-terracotta mb-4">
              Getting Here
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-spa-slate max-w-xl">
              Nearby landmarks & directions
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            {LANDMARKS.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.08}>
                <div className="bg-spa-surface rounded-3xl p-7 h-full border border-spa-slate/5">
                  <l.icon className="w-7 h-7 text-spa-sage" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg text-spa-slate mt-5">
                    {l.title}
                  </h3>
                  <p className="text-sm text-spa-textMuted mt-2 leading-relaxed">
                    {l.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-28 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl text-spa-slate">
            We'd love to welcome you
          </h2>
          <a
            href={`tel:${BUSINESS.phoneClean}`}
            data-testid="contact-final-call-button"
            className="inline-flex items-center gap-2.5 bg-spa-terracotta text-spa-cream px-8 py-4 rounded-full text-sm font-medium mt-8 hover:bg-spa-slate transition-all duration-300"
          >
            <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
          </a>
        </Reveal>
      </section>
    </>
  );
};

export default Contact;
